import React from 'react';
import ms from 'milsymbol';
import { SidcOption, SidcParts, buildSidc } from './sidcOptions';

/** Icon size in pixels for dropdown display */
const ICON_SIZE = 24;

/**
 * Renders a military symbol icon for a unit type option.
 *
 * Uses the milsymbol library to generate NATO APP-6D standard symbols.
 * The icon reflects the current SIDC context (identity, echelon, etc.)
 * combined with the specific unit type.
 *
 * @param option - The unit type option to render an icon for
 * @param currentSidc - Current SIDC parts for context (identity, echelon, etc.)
 * @returns A React node containing the SVG symbol
 *
 * @example
 * const iconRenderer = useCallback(
 *   (option: SidcOption) => renderUnitTypeIcon(option, parts),
 *   [parts.identity, parts.echelon, parts.status, parts.hqtfd]
 * );
 */
export function renderUnitTypeIcon(
    option: SidcOption,
    currentSidc: SidcParts
): React.ReactNode {
    // Build a complete SIDC for this unit type using current context
    const iconSidc = buildSidc(
        currentSidc.identity,
        currentSidc.symbolSet,
        currentSidc.status,
        currentSidc.hqtfd,
        currentSidc.echelon,
        option.value.padEnd(10, '0')
    );

    // Generate the symbol SVG
    const symbol = new ms.Symbol(iconSidc, { size: ICON_SIZE });
    const svgString = symbol.asSVG();

    return (
        <span
            className="unit-type-icon"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: `${ICON_SIZE}px`,
                height: `${ICON_SIZE}px`,
            }}
            // Using dangerouslySetInnerHTML is safe here because:
            // 1. milsymbol is a trusted library that generates SVGs
            // 2. The input (SIDC code) is validated numeric string
            // 3. No user-controlled content is injected
            dangerouslySetInnerHTML={{ __html: svgString }}
            data-testid={`unit-type-icon-${option.value}`}
        />
    );
}

/**
 * Creates a memoizable icon renderer function for use with IconDropdown.
 *
 * This is a factory function that creates an icon renderer bound to
 * specific SIDC parts. Use this with useCallback to memoize the renderer.
 *
 * @param currentSidc - Current SIDC parts for context
 * @returns A function that renders icons for unit type options
 *
 * @example
 * const iconRenderer = useMemo(
 *   () => createUnitTypeIconRenderer(parts),
 *   [parts.identity, parts.echelon, parts.status, parts.hqtfd, parts.symbolSet]
 * );
 */
export function createUnitTypeIconRenderer(
    currentSidc: SidcParts
): (option: SidcOption) => React.ReactNode {
    return (option: SidcOption) => renderUnitTypeIcon(option, currentSidc);
}
